import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {Panel, PanelHeader} from '@Components/panel';
import {useUpdateEffect} from 'ahooks';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {ThumbnailItemSelectionState} from '@Components/thumbnail-item';
import {QrBackgroundColorOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/qr-background-color-option';
import {updateOptionsPanelHeight} from '@Components/poster-editor/library/poster-editor-library';
import PosterEditorColorPanel from '@Panels/poster-editor-color-panel';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import type {RGBA} from '@Utils/color.util';
import {ContextualMenu} from '@Components/contextual-menu';
import {ContextualMenuThumbnailItem} from '@Components/contextual-menu/components/contextual-menu-thumbnail-item';
import styles from './poster-editor-qr-content-background-panel.module.scss';
import {useGetKeyFromQRItem} from '@Hooks/poster-editor/useGetKeyFromQRItem';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';

interface PosterEditorQrContentBackgroundPanelProps {
  panelId: string;
}

enum QRBackgroundPanels {
  DEFAULT = 'DEFAULT',
  COLOR_PANEL = 'COLOR_PANEL',
}

const DEFAULT_HEIGHT = 158;
const DEFAULT_COLOR_BG_HEIGHT = 206;

export default function PosterEditorQrContentBackgroundPanel({...props}: PosterEditorQrContentBackgroundPanelProps): ReactElement | null {
  const onClose = useDefaultPanelClose(props.panelId);
  const [panelToShow, setPanelToShow] = useState(QRBackgroundPanels.DEFAULT);
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const isBgTransparent = useGetKeyFromQRItem('isBackgroundTransparent');
  const qrBackgroundColor = useGetKeyFromQRItem('qrBackgroundColor');

  useUpdateEffect((): void => {
    onClose();
  }, [activeItem]);

  useEffect((): void => {
    if (!activeItem || !activeItem.isQRItem() || isBgTransparent === undefined) {
      return;
    }
    updateHeight(isBgTransparent);
  }, [isBgTransparent]);

  if (!activeItem || !activeItem.isQRItem() || isBgTransparent === undefined || !qrBackgroundColor) {
    return null;
  }

  const getHeader = (): ReactElement => {
    return <PanelHeader onClose={onClose} title={window.i18next.t('pmwjs_background')} />;
  };

  const updateHeight = (isTransparent: boolean): void => {
    if (isTransparent) {
      updateOptionsPanelHeight(props.panelId, `${DEFAULT_HEIGHT}px`);
    } else {
      updateOptionsPanelHeight(props.panelId, `${DEFAULT_COLOR_BG_HEIGHT}px`);
    }
  };

  const updateBackgroundState = (): void => {
    updateHeight(!isBgTransparent);
    activeItem.toggleBackgroundStateForQRItem();
  };

  const getThumbnails = (): ReactElement => {
    return (
      <ul className={`flex-h-row content-body spacing-m-0 ${isBgTransparent ? '' : 'spacing-m-t-4'}`}>
        <ContextualMenuThumbnailItem
          thumbnail={<Icon icon="icon-qr-content" size={IconSize.SIZE_ICON_32} />}
          isSelected={isBgTransparent}
          selectionState={ThumbnailItemSelectionState.BORDER}
          text={window.i18next.t('pmwjs_none')}
          onClick={(): void => {
            if (!isBgTransparent) {
              updateBackgroundState();
            }
          }}
        />
        <ContextualMenuThumbnailItem
          thumbnail={<Icon icon="icon-qr-content" size={IconSize.SIZE_ICON_32} className={styles.selectedQrIcon} />}
          isSelected={!isBgTransparent}
          selectionState={ThumbnailItemSelectionState.BORDER}
          text={window.i18next.t('pmwjs_background_solid')}
          onClick={(): void => {
            if (isBgTransparent) {
              updateBackgroundState();
            }
          }}
        />
      </ul>
    );
  };

  const getColorOption = (): ReactElement | null => {
    if (isBgTransparent) {
      return null;
    }
    return (
      <QrBackgroundColorOption
        onClickColorFill={(): void => {
          setPanelToShow(QRBackgroundPanels.COLOR_PANEL);
        }}
      />
    );
  };

  const getContent = (): ReactElement => {
    return (
      <ContextualMenu id="qr-background-mobile-options" hasMobileModalPadding hasMobileMorphingProperties>
        {getColorOption()}
        {getThumbnails()}
      </ContextualMenu>
    );
  };

  const updateQRBackgroundColor = (fillColor: RGBA[], undoable = true): void => {
    activeItem.updateQRBackgroundColor(fillColor[0], undoable);
  };

  const getColorPanel = (): ReactElement => {
    return (
      <PosterEditorColorPanel
        headerText={window.i18next.t('pmwjs_background_solid')}
        onBackButton={(): void => {
          updateHeight(isBgTransparent);
          setPanelToShow(QRBackgroundPanels.DEFAULT);
        }}
        panelId={props.panelId}
        fillObject={{
          fillType: FillTypes.SOLID,
          fillColor: [qrBackgroundColor],
        }}
        onChangeColor={updateQRBackgroundColor}
        onClose={(): void => {
          onClose();
        }}
      />
    );
  };

  const getPanels = (): ReactElement => {
    switch (panelToShow) {
      case QRBackgroundPanels.COLOR_PANEL:
        return getColorPanel();
      case QRBackgroundPanels.DEFAULT:
      default:
        return <Panel panelHeader={getHeader()} panelContent={getContent()} />;
    }
  };

  return getPanels();
}
